import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload as UploadIcon, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { patientAPI, fileAPI } from '../utils/api';

export default function CaseUploadWizard({ onComplete }) {
  const [step, setStep] = useState(1);
  const [doctors, setDoctors] = useState([]);
  const [formData, setFormData] = useState({
    exam_type: '',
    doctor_id: '',
    notes: '',
    is_urgent: false,
  });
  const [files, setFiles] = useState([]);
  const [caseId, setCaseId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await patientAPI.getDoctors();
      setDoctors(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить список врачей');
    }
  };

  const examTypes = [
    'КТ сканирование',
    'МРТ',
    'ПЭТ-КТ',
    'Рентген',
    'УЗИ',
    'Маммография',
    'Другое'
  ];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleNext = async () => {
    if (step === 1 && !formData.exam_type) {
      toast.error('Пожалуйста, выберите тип обследования');
      return;
    }
    if (step === 2 && !formData.doctor_id) {
      toast.error('Пожалуйста, выберите врача');
      return;
    }
    if (step === 3 && files.length === 0) {
      toast.error('Пожалуйста, загрузите хотя бы один файл');
      return;
    }

    if (step === 3) {
      // Create case and upload files
      setUploading(true);
      try {
        const caseResponse = await patientAPI.createCase(formData);
        const newCaseId = caseResponse.data.id;
        setCaseId(newCaseId);

        // Upload files
        for (const file of files) {
          await fileAPI.upload(newCaseId, file);
        }

        toast.success('Кейс успешно создан!');
        setStep(4);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Не удалось создать кейс');
      } finally {
        setUploading(false);
      }
      return;
    }

    setStep(step + 1);
  };

  const selectedDoctor = doctors.find(d => d.id === formData.doctor_id);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Button
        data-testid="back-button"
        onClick={() => step === 1 ? navigate('/patient') : setStep(step - 1)}
        variant="ghost"
        className="mb-6 text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Назад
      </Button>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {['Тип обследования', 'Выбор врача', 'Загрузка файлов', 'Проверка'].map((label, idx) => (
            <div key={idx} className="flex-1 text-center">
              <div className={`text-xs font-medium ${step > idx + 1 ? 'text-primary' : step === idx + 1 ? 'text-slate-900' : 'text-slate-500'}`}>
                {label}
              </div>
            </div>
          ))}
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Step 1: Exam Type */}
      {step === 1 && (
        <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm">
          <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Выберите тип обследования</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {examTypes.map((type) => (
              <button
                key={type}
                data-testid={`exam-type-${type.toLowerCase().replace(/[\s-]/g, '-')}`}
                onClick={() => setFormData({ ...formData, exam_type: type })}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.exam_type === type
                    ? 'border-primary bg-primary-50 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className={`font-semibold ${
                  formData.exam_type === type ? 'text-primary' : 'text-slate-900'
                }`}>
                  {type}
                </div>
              </button>
            ))}
          </div>
          <div className="mb-6">
            <Label htmlFor="notes" className="text-slate-900 font-medium mb-2 block">Дополнительные заметки (необязательно)</Label>
            <textarea
              id="notes"
              data-testid="case-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Любые конкретные проблемы или информация для радиолога..."
            />
          </div>
          <Button
            data-testid="next-step-button"
            onClick={handleNext}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 h-11"
          >
            Далее
          </Button>
        </Card>
      )}

      {/* Step 2: Select Doctor */}
      {step === 2 && (
        <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm">
          <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Выберите радиолога</h2>
          
          {/* Urgent Option */}
          <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="urgent"
                data-testid="urgent-checkbox"
                checked={formData.is_urgent}
                onChange={(e) => setFormData({ ...formData, is_urgent: e.target.checked })}
                className="mt-1 w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
              />
              <div className="flex-1">
                <label htmlFor="urgent" className="font-semibold text-slate-900 cursor-pointer block mb-1">
                  🚀 Срочный ответ в течение 24 часов (+$20)
                </label>
                <p className="text-sm text-slate-600">
                  Получите экспертное заключение в течение 24 часов вместо стандартных 72 часов
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {doctors.map((doctor) => (
              <button
                key={doctor.id}
                data-testid={`doctor-${doctor.id}`}
                onClick={() => setFormData({ ...formData, doctor_id: doctor.id })}
                className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                  formData.doctor_id === doctor.id
                    ? 'border-primary bg-primary-50 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex gap-4 flex-1">
                    {doctor.photo_url && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${doctor.photo_url}`}
                        alt={`${doctor.first_name} ${doctor.last_name}`}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold font-heading text-slate-900 mb-1">
                        {doctor.first_name} {doctor.last_name}
                      </h3>
                      <p className="text-sm text-slate-600 mb-2">{doctor.specialty}</p>
                      <p className="text-sm text-slate-500">{doctor.experience_years} лет опыта</p>
                      {doctor.bio && (
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{doctor.bio}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xl font-bold text-primary">${doctor.price_per_case}</div>
                    {formData.is_urgent && (
                      <div className="text-sm text-amber-600 font-semibold mt-1">+ $20 срочно</div>
                    )}
                    <div className="text-xs text-slate-500">за кейс</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <Button
            data-testid="next-step-button"
            onClick={handleNext}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 h-11"
          >
            Далее
          </Button>
        </Card>
      )}

      {/* Step 3: Upload Files */}
      {step === 3 && (
        <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm">
          <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Загрузить медицинские файлы</h2>
          <div className="file-upload-area rounded-xl p-12 text-center mb-6">
            <UploadIcon className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">Перетащите файлы или нажмите для выбора</p>
            <Input
              data-testid="file-upload-input"
              type="file"
              multiple
              accept=".dcm,.pdf,.zip,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="max-w-xs mx-auto"
            />
            <p className="text-xs text-slate-500 mt-4">Поддерживаются: DICOM, PDF, ZIP, JPG, PNG</p>
          </div>
          {files.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-slate-900 mb-2">Выбранные файлы:</h3>
              <ul className="space-y-2">
                {files.map((file, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600 p-2 bg-slate-50 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-semantic-success" />
                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} МБ)
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button
            data-testid="upload-files-button"
            onClick={handleNext}
            disabled={uploading}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 h-11"
          >
            {uploading ? 'Загрузка...' : 'Загрузить и продолжить'}
          </Button>
        </Card>
      )}

      {/* Step 4: Review & Payment */}
      {step === 4 && (
        <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm text-center">
          <CheckCircle className="w-16 h-16 text-semantic-success mx-auto mb-4" />
          <h2 className="text-2xl font-bold font-heading text-slate-900 mb-4">Кейс успешно создан!</h2>
          <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-slate-500 mb-1">Тип обследования</div>
                <div className="font-semibold text-slate-900">{formData.exam_type}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-1">Врач</div>
                <div className="font-semibold text-slate-900">{selectedDoctor?.full_name}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-1">Загружено файлов</div>
                <div className="font-semibold text-slate-900">{files.length}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-1">Общая стоимость</div>
                <div className="font-semibold text-primary text-xl">
                  ${selectedDoctor?.price_per_case + (formData.is_urgent ? 20 : 0)}
                  {formData.is_urgent && (
                    <span className="text-sm text-amber-600 block">включая срочную обработку (+$20)</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <p className="text-slate-600 mb-6">
            Завершите оплату, чтобы отправить этот кейс на проверку. Врач будет уведомлен после подтверждения оплаты.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              data-testid="complete-payment-button"
              onClick={() => {
                // Payment will be handled in the main dashboard
                onComplete();
              }}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 h-11"
            >
              Перейти в панель управления
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
